/**
 * Lightweight reconnecting WebSocket helper for live order events.
 *
 * Auth flow (preferred — JWT never appears in access logs):
 *   1. POST /ws/ticket  (REST, Authorization: Bearer <jwt>)  → { ticket }
 *   2. ws://.../ws?ticket=<ticket>&restaurant_id=<uuid>
 *
 * Fallback (legacy — used if ticket fetch fails):
 *   ws://.../ws?token=<jwt>&restaurant_id=<uuid>
 */

import { api, getToken } from "./api";

export type WsMessage =
  | { event: "order.received" | "order.updated"; order_id: string; restaurant_id: string }
  | { event: string; order_id?: string; restaurant_id?: string; [k: string]: unknown };

function wsBase(): string {
  return (
    (import.meta.env.VITE_WS_BASE as string | undefined) ??
    `${window.location.protocol === "https:" ? "wss:" : "ws:"}//${window.location.host}/ws`
  );
}

export function openOrderStream(
  restaurantId: string,
  onMessage: (msg: WsMessage) => void,
): () => void {
  const token = getToken();
  if (!token) return () => undefined;

  let ws: WebSocket | null = null;
  let stopped = false;
  let backoff = 1000;

  const openSocket = (url: string) => {
    if (stopped) return;
    ws = new WebSocket(url);
    ws.onopen = () => {
      backoff = 1000;
    };
    ws.onmessage = (ev) => {
      try {
        onMessage(JSON.parse(ev.data) as WsMessage);
      } catch {
        /* ignore malformed frames */
      }
    };
    ws.onclose = () => {
      if (stopped) return;
      setTimeout(connect, backoff);
      backoff = Math.min(backoff * 2, 15000);
    };
    ws.onerror = () => ws?.close();
  };

  const connect = () => {
    if (stopped) return;
    api
      .getWsTicket(restaurantId)
      .then(({ ticket }) => {
        openSocket(`${wsBase()}?ticket=${encodeURIComponent(ticket)}&restaurant_id=${restaurantId}`);
      })
      .catch(() => {
        openSocket(`${wsBase()}?token=${encodeURIComponent(token!)}&restaurant_id=${restaurantId}`);
      });
  };

  connect();
  return () => {
    stopped = true;
    ws?.close();
  };
}
